import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ErrorNotice } from './Layout';
import { useMe } from './AuthGuard';

type Settings = {
  settings: { calendarId: string; enabled: boolean } | null;
  delegates: Array<{ id: string; email: string; created_at: string; last_used_at: string | null }>;
  recent: Array<{ id: string; title: string; start_at: string; google_event_url: string | null; delegate_email: string }>;
};
type Calendar = { id: string; name: string; accessRole: string; primary?: boolean };

export function CalendarDelegationSettings() {
  const me = useMe();
  const client = useQueryClient();
  const [email, setEmail] = useState('');
  const [calendarId, setCalendarId] = useState('');
  const data = useQuery({ queryKey: ['calendar-delegation'], queryFn: () => api<{ data: Settings }>('/api/calendar-delegation').then((r) => r.data) });
  const calendars = useQuery({ queryKey: ['calendars'], queryFn: () => api<{ data: Calendar[] }>('/api/calendars').then((r) => r.data), enabled: Boolean(me.data?.googleConnected) });
  const selected = calendarId || data.data?.settings?.calendarId || calendars.data?.find((item) => item.primary)?.id || calendars.data?.[0]?.id || '';
  const refresh = () => client.invalidateQueries({ queryKey: ['calendar-delegation'] });
  const save = useMutation({
    mutationFn: (enabled: boolean) => api('/api/calendar-delegation', { method: 'PUT', body: JSON.stringify({ calendarId: selected, enabled }) }),
    onSuccess: refresh,
  });
  const add = useMutation({
    mutationFn: () => api('/api/calendar-delegation/delegates', { method: 'POST', body: JSON.stringify({ email }) }),
    onSuccess: async () => { setEmail(''); await refresh(); },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/calendar-delegation/delegates/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });
  const error = data.error || calendars.error || save.error || add.error || revoke.error;
  return (
    <div className="card space-y-4">
      <div>
        <h2 className="font-semibold">ผู้ช่วยลงนัดใน Google Calendar</h2>
        <p className="mt-1 text-sm text-slate-600">ผู้ช่วยเข้าด้วย Google ของตนเอง ลงนัดได้ทันที แต่ไม่เห็นเอกสารหรือรายการของคุณ ถอนสิทธิ์ได้ที่นี่</p>
      </div>
      {!me.data?.googleConnected && <p className="text-sm text-amber-800">ต้องเชื่อมต่อ Google Calendar ของเจ้าของก่อนเปิดสิทธิ์ผู้ช่วย</p>}
      <label className="block text-sm font-medium">ปฏิทินปลายทาง
        <select className="field mt-1" value={selected} onChange={(event) => setCalendarId(event.target.value)} disabled={!calendars.data?.length}>
          {calendars.data?.map((item) => <option key={item.id} value={item.id}>{item.name} ({item.id})</option>)}
        </select>
      </label>
      <button className="btn-primary w-full" disabled={!me.data?.googleConnected || !selected || save.isPending} onClick={() => save.mutate(true)}>
        {data.data?.settings?.enabled ? 'บันทึกปฏิทินปลายทาง' : 'เปิดสิทธิ์ผู้ช่วย'}
      </button>
      {data.data?.settings?.enabled && <button className="btn-secondary w-full" disabled={save.isPending} onClick={() => save.mutate(false)}>พักสิทธิ์ผู้ช่วยทั้งหมด</button>}
      {data.data?.settings?.enabled && <>
        <div className="border-t pt-4">
          <label className="block text-sm font-medium">เพิ่มอีเมลผู้ช่วย</label>
          <div className="mt-2 flex gap-2">
            <input type="email" className="field min-w-0 flex-1" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="assistant@example.com" autoComplete="off" />
            <button className="btn-primary" disabled={!email.trim() || add.isPending} onClick={() => add.mutate()}>เพิ่ม</button>
          </div>
          <p className="mt-1 text-xs text-slate-500">ส่งลิงก์หน้าเข้าสู่ระบบให้ผู้ช่วยเอง ระบบจะไม่ส่งคำเชิญทางอีเมล</p>
        </div>
        <div className="space-y-2">
          {data.data?.delegates.length ? data.data.delegates.map((person) => <div key={person.id} className="flex items-center justify-between gap-2 rounded-xl border p-3 text-sm">
            <span className="min-w-0 break-all">{person.email}</span>
            <button className="shrink-0 font-semibold text-red-700" disabled={revoke.isPending} onClick={() => { if (confirm(`ถอนสิทธิ์ ${person.email}?`)) revoke.mutate(person.id); }}>ถอนสิทธิ์</button>
          </div>) : <p className="text-sm text-slate-500">ยังไม่มีผู้ช่วย</p>}
        </div>
      </>}
      {!!data.data?.recent.length && <details className="border-t pt-3"><summary className="cursor-pointer font-medium">ประวัติที่ผู้ช่วยลงนัด</summary>
        <div className="mt-2 space-y-2">{data.data.recent.map((event) => <div key={event.id} className="rounded-xl border p-2 text-sm">
          <p className="font-medium">{event.title}</p><p className="text-slate-600">{event.start_at} · {event.delegate_email}</p>
          {event.google_event_url && <a className="text-teal-700 underline" href={event.google_event_url} target="_blank" rel="noreferrer">เปิดใน Google Calendar</a>}
        </div>)}</div>
      </details>}
      {error && <ErrorNotice message={error.message} />}
    </div>
  );
}
